import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { fetch } from "@/src/lib/fetch";
import { Button } from "../../../components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel } from "../../../components/ui/form";
import { UploadInput } from "../../../components/ui/upload";
import { Textarea } from "../../../components/ui/textarea";
import { Combobox } from "../../../components/ui/combobox";
import { extractFromMultiName } from "../../Locations/utils";
import { Location } from "@/src/app/Locations/model";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect } from "react";
import { toast } from "../../../components/ui/use-toast";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { BoardingInfoForm, emptyBoardingInfoForm } from "./operator.schema";

export const AddNewBoardingInfoModal = ({
  opened,
  setOpened,
  currentLocations,
}: {
  opened: boolean;
  setOpened: (opened: boolean) => void;
  currentLocations: string[];
}) => {
  const form = useForm<BoardingInfoForm>({
    resolver: zodResolver(BoardingInfoForm),
    defaultValues: emptyBoardingInfoForm,
    mode: "onTouched",
    reValidateMode: "onChange",
  });

  const { id: operatorId } = useParams();

  const queryClient = useQueryClient();

  useEffect(() => {
    if (!opened) {
      form.setValue("description", "");
      form.setValue("location", {
        id: "",
        names: "",
      });
      form.setValue("file", null);
    }
  }, [opened]);

  const onSubmit = useCallback(
    form.handleSubmit(
      async (data) => {
        try {
          await fetch<{ data: { id: string } }>(`/api/operators/boarding-info`, "POST", {
            description: data.description,
            locationId: data.location.id,
            file: data.file,
            operatorId,
          }).then(async (res) => {
            toast({
              title: "Boarding info saved",
              description: "New boarding info was saved",
              variant: "default",
            });
            await queryClient.invalidateQueries({ queryKey: ["operator", operatorId] });
            form.setValue("description", "");
            form.setValue("location", {
              id: "",
              names: "",
            });
            form.setValue("file", null);
            setOpened(false);
          });
        } catch (e: any) {
          console.error(e);
          toast({
            title: "Failed to save the boarding info",
            description: e.message || "An error occurred while saving the operator",
            variant: "destructive",
          });
          form.setValue("description", "");
          form.setValue("location", {
            id: "",
            names: "",
          });
          form.setValue("file", null);
          setOpened(false);
        }
      },
      () => {
        console.error(form, form.getValues());
      },
    ),
    [form],
  );
  return (
    <Dialog open={opened} onOpenChange={setOpened}>
      <DialogTrigger>
        <Button
          className="flex"
          variant="secondary"
          onClick={(e) => {
            e.preventDefault();
            setOpened(true);
          }}
        >
          Add new boarding info
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[620px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-medium">Add new boarding information</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form className="flex flex-col gap-6" onSubmit={onSubmit}>
            <div className="flex flex-row flex-wrap gap-4 items-start">
              <FormField<BoardingInfoForm, "location">
                name="location"
                render={({ field }) => {
                  return (
                    <FormItem className="w-full">
                      <FormLabel>Location</FormLabel>
                      <FormControl>
                        <Combobox<Pick<Location, "id" | "names">>
                          className="w-full"
                          placeholder="Search for location"
                          loadOptions={(query) =>
                            fetch<{ data: Location[] }>(
                              `/api/locations/locations?pageSize=5${query ? `&filter=${query}` : ""}`,
                            ).then((r) => r.data?.filter((l) => !currentLocations.includes(l.id)))
                          }
                          value={field.value?.id ? field.value : null}
                          onChange={(v) => {
                            field.onChange(v && { id: v.id, names: v.names });
                          }}
                          renderOption={(r) => extractFromMultiName(r.names)}
                        />
                      </FormControl>
                    </FormItem>
                  );
                }}
              />
              <FormField<BoardingInfoForm, "file">
                name={`file`}
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>File</FormLabel>
                    <FormControl>
                      <UploadInput
                        className="bg-white px-8"
                        accept="image/svg+xml, image/png, application/pdf"
                        onChange={field.onChange}
                        value={field.value}
                        ownerType="boarding_info"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <FormField<BoardingInfoForm, "description">
              name={"description"}
              render={({ field }) => (
                <FormItem className="flex-1 min-w-52">
                  <FormLabel>Boarding information</FormLabel>
                  <FormControl>
                    <Textarea className="resize-none" rows={7} {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
          </form>
        </Form>
        <DialogFooter className="flex mt-8 w-full !justify-between">
          <Button onClick={onSubmit}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
